export const applyOutFocusMode = async (canvas, ctx, setMessage, language) => {
  try {
    // 1단계: 피사체 영역 정의
    setMessage(language === 'KR' ? '아웃포커스 모드 적용 중 (1/4) - 피사체 영역 설정...' : 'Applying Out-Focus Mode (1/4) - Setting subject area...');
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // 2단계: 배경 블러 생성
    setMessage(language === 'KR' ? '아웃포커스 모드 적용 중 (2/4) - 배경 블러 생성...' : 'Applying Out-Focus Mode (2/4) - Creating background blur...');
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // 원본 이미지 데이터 가져오기
    const originalImageData = ctx.getImageData(0, 0, width, height);
    const originalData = originalImageData.data;
    
    // 강한 블러 효과를 위한 임시 캔버스 생성
    const blurCanvas = document.createElement('canvas');
    blurCanvas.width = width;
    blurCanvas.height = height;
    const blurCtx = blurCanvas.getContext('2d');
    
    // 원본 이미지를 블러 캔버스에 복사
    blurCtx.drawImage(canvas, 0, 0);
    
    // CSS 필터를 사용한 강한 블러 효과
    blurCtx.filter = 'blur(20px)';
    blurCtx.globalCompositeOperation = 'source-over';
    blurCtx.drawImage(canvas, 0, 0);
    
    // 블러된 이미지 데이터 가져오기
    const blurredImageData = blurCtx.getImageData(0, 0, width, height);
    const blurredData = blurredImageData.data;
    
    // 3단계: 피사체 마스크 적용
    setMessage(language === 'KR' ? '아웃포커스 모드 적용 중 (3/4) - 피사체 마스크 적용...' : 'Applying Out-Focus Mode (3/4) - Applying subject mask...');
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // 결과 이미지 데이터 생성
    const resultData = new Uint8ClampedArray(originalData);
    
    // 피사체 영역 정의 (타원형)
    const focusRadiusX = width * 0.35;  // 가로 반지름
    const focusRadiusY = height * 0.45; // 세로 반지름
    
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const pos = (y * width + x) * 4;
        
        // 중심점에서의 거리 계산 (타원)
        const dx = (x - centerX) / focusRadiusX;
        const dy = (y - centerY) / focusRadiusY;
        const ellipseDistance = Math.sqrt(dx * dx + dy * dy);
        
        // 블렌딩 팩터 계산 (부드러운 전환)
        let blendFactor;
        
        if (ellipseDistance <= 0.6) {
          // 중심 영역: 완전히 선명
          blendFactor = 1.0;
        } else if (ellipseDistance <= 1.2) {
          // 전환 영역: 부드러운 그라데이션
          const t = (ellipseDistance - 0.6) / 0.6;
          // 부드러운 S-커브 적용
          const smoothT = t * t * (3 - 2 * t);
          blendFactor = 1.0 - smoothT;
        } else {
          // 배경 영역: 완전히 블러
          blendFactor = 0.0;
        }
        
        // 원본과 블러 이미지 혼합
        for (let c = 0; c < 3; c++) {
          resultData[pos + c] = Math.round(
            originalData[pos + c] * blendFactor + 
            blurredData[pos + c] * (1 - blendFactor)
          );
        }
        // 알파 채널은 원본 유지
        resultData[pos + 3] = originalData[pos + 3];
      }
    }
    
    // 4단계: 피사체 선명도 강화
    setMessage(language === 'KR' ? '아웃포커스 모드 적용 중 (4/4) - 피사체 선명도 강화...' : 'Applying Out-Focus Mode (4/4) - Enhancing subject sharpness...');
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // 중심 영역에만 선택적 샤프닝 적용
    const sharpenedData = new Uint8ClampedArray(resultData);
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const pos = (y * width + x) * 4;
        
        // 중심점에서의 거리 계산
        const dx = (x - centerX) / focusRadiusX;
        const dy = (y - centerY) / focusRadiusY;
        const ellipseDistance = Math.sqrt(dx * dx + dy * dy);
        
        // 피사체 영역에만 샤프닝 적용
        if (ellipseDistance <= 0.8) {
          const sharpenStrength = Math.max(0, 1 - ellipseDistance / 0.8) * 0.3;
          
          for (let c = 0; c < 3; c++) {
            const current = resultData[pos + c];
            const top = resultData[pos - width * 4 + c];
            const bottom = resultData[pos + width * 4 + c];
            const left = resultData[pos - 4 + c];
            const right = resultData[pos + 4 + c];
            
            // 언샵 마스킹
            const average = (top + bottom + left + right) / 4;
            const sharpened = current + (current - average) * sharpenStrength;
            
            sharpenedData[pos + c] = Math.min(255, Math.max(0, Math.round(sharpened)));
          }
        }
      }
    }
    
    // 최종 결과를 캔버스에 적용
    const finalImageData = new ImageData(sharpenedData, width, height);
    ctx.putImageData(finalImageData, 0, 0);
    
    // 미세한 색상 보정 (피사체 강조)
    applySubtleColorCorrection(ctx, width, height, centerX, centerY, focusRadiusX, focusRadiusY);
    
    setMessage(language === 'KR' ? '아웃포커스 모드 적용 완료!' : 'Out-Focus Mode applied!');
    
  } catch (error) {
    console.error('Error in applyOutFocusMode:', error);
    setMessage(language === 'KR' ? '처리 중 오류가 발생했습니다.' : 'An error occurred during processing.');
    throw error;
  }
};

// 미세한 색상 보정 함수
function applySubtleColorCorrection(ctx, width, height, centerX, centerY, radiusX, radiusY) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pos = (y * width + x) * 4;
      
      // 중심점에서의 거리 계산
      const dx = (x - centerX) / radiusX;
      const dy = (y - centerY) / radiusY;
      const ellipseDistance = Math.sqrt(dx * dx + dy * dy);
      
      if (ellipseDistance <= 0.8) {
        // 피사체 영역: 약간의 채도와 대비 증가
        const strength = Math.max(0, 1 - ellipseDistance / 0.8) * 0.1;
        
        const r = data[pos];
        const g = data[pos + 1];
        const b = data[pos + 2];
        
        // 채도 증가
        const avg = (r + g + b) / 3;
        data[pos] = Math.min(255, Math.max(0, avg + (r - avg) * (1 + strength)));
        data[pos + 1] = Math.min(255, Math.max(0, avg + (g - avg) * (1 + strength)));
        data[pos + 2] = Math.min(255, Math.max(0, avg + (b - avg) * (1 + strength)));
        
      } else if (ellipseDistance > 1.2) {
        // 배경 영역: 약간의 채도 감소
        const r = data[pos];
        const g = data[pos + 1];
        const b = data[pos + 2];
        
        const avg = (r + g + b) / 3;
        const desaturation = 0.05;
        
        data[pos] = Math.round(r * (1 - desaturation) + avg * desaturation);
        data[pos + 1] = Math.round(g * (1 - desaturation) + avg * desaturation);
        data[pos + 2] = Math.round(b * (1 - desaturation) + avg * desaturation);
      }
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
}